import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { DashboardHeader, StatusBadge } from "../components/DashboardHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { fmtDateLong, groupByDate, WEEKDAYS, todayISO } from "../lib/dates";
import { CalendarClock, Clock, Loader2, Check, X, Trash2, Plus, CalendarDays, CalendarRange, Repeat, Users, CheckCircle2, Hourglass, Building2, Mail, Phone, FileCheck2, UserPlus, Pencil, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { AgendaCalendar } from "../components/AgendaCalendar";
import { CompletedReportRow } from "../components/CompletedReportRow";
import { ClinicDialog } from "../components/ClinicDialog";
import { TimeSelect } from "../components/TimeSelect";
import { StudySearch } from "../components/StudySearch";
import { filterStudies } from "../lib/studies";
import { History, Download, FileArchive, FileSpreadsheet, Euro, LogIn, ShieldCheck, ShieldAlert } from "lucide-react";
import { downloadExport } from "../lib/download";
import { TariffsManager } from "../components/TariffsManager";
import { PatientDetails } from "../components/PatientDetails";
import { GoogleCalendarCard } from "../components/GoogleCalendarCard";
import { CancelBookingButton } from "../components/CancelBookingButton";
import { RescheduleBookingButton } from "../components/RescheduleBookingButton";
import { AdminBookingDialog } from "../components/AdminBookingDialog";

export default function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [rules, setRules] = useState([]);
  const [slots, setSlots] = useState([]);
  const [stats, setStats] = useState(null);
  const [blockedDays, setBlockedDays] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [activeTab, setActiveTab] = useState("agenda");
  const [clinicDialog, setClinicDialog] = useState({ open: false, clinic: null });
  const [historyFilter, setHistoryFilter] = useState({ q: "", clinic_id: "", from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [rule, setRule] = useState({ weekday: "0", start_time: "09:00", end_time: "14:00", slot_minutes: "30" });
  const [block, setBlock] = useState({ start_date: todayISO(), end_date: todayISO(), weekdays: [0, 1, 2, 3, 4], start_time: "09:00", end_time: "14:00", slot_minutes: "30" });

  const load = useCallback(async () => {
    try {
      const [b, r, s, st, bd, cl] = await Promise.all([
        api.get("/bookings"), api.get("/availability/rules"), api.get("/slots"), api.get("/stats"), api.get("/blocked-days"), api.get("/clinics"),
      ]);
      setBookings(b.data); setRules(r.data); setSlots(s.data); setStats(st.data); setBlockedDays(bd.data); setClinics(cl.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addRule = () => act(async () => {
    await api.post("/availability/rules", {
      weekday: parseInt(rule.weekday), start_time: rule.start_time,
      end_time: rule.end_time, slot_minutes: parseInt(rule.slot_minutes),
    });
  }, "Horario añadido y franjas generadas");

  const toggleWeekday = (i) => {
    setBlock((b) => ({ ...b, weekdays: b.weekdays.includes(i) ? b.weekdays.filter((x) => x !== i) : [...b.weekdays, i].sort() }));
  };

  const addRange = () => act(async () => {
    const { data } = await api.post("/availability/range", {
      start_date: block.start_date, end_date: block.end_date, weekdays: block.weekdays,
      start_time: block.start_time, end_time: block.end_time, slot_minutes: parseInt(block.slot_minutes),
    });
    if (data.created === 0) throw new Error("No se generaron franjas nuevas (revisa fechas/días)");
  }, "Bloque de días añadido y franjas generadas");

  const pending = bookings.filter((b) => b.status === "pending");
  const others = bookings.filter((b) => b.status !== "pending");
  const confirmed = bookings.filter((b) => b.status === "confirmed");
  const completed = bookings.filter((b) => b.status === "completed");
  const unreadCount = completed.reduce((s, b) => s + (b.unread || 0), 0);
  const filteredCompleted = filterStudies(completed, historyFilter);
  const grouped = groupByDate(slots);

  const openClinicHistory = (clinicId) => {
    setHistoryFilter({ q: "", clinic_id: clinicId, from: "", to: "" });
    setActiveTab("completed");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [exporting, setExporting] = useState("");
  const runExport = async (kind) => {
    setExporting(kind);
    const params = { clinic_id: historyFilter.clinic_id || undefined, from_date: historyFilter.from || undefined, to_date: historyFilter.to || undefined, q: historyFilter.q || undefined };
    try {
      if (kind === "zip") await downloadExport("/export/reports.zip", params, "veteo_informes.zip");
      else await downloadExport("/export/studies.csv", params, "veteo_estudios.csv");
      toast.success(kind === "zip" ? "Copia de seguridad (ZIP) descargada" : "CSV descargado");
    } catch (e) {
      let msg = "No se pudo generar la exportación";
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      toast.error(msg);
    } finally { setExporting(""); }
  };

  const exportClinics = async () => {
    setExporting("clinics");
    try {
      await downloadExport("/export/clinics.csv", {}, "veteo_clinicas.csv");
      toast.success("Listado de clínicas descargado");
    } catch (e) {
      toast.error("No se pudo exportar el listado de clínicas");
    } finally { setExporting(""); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAFC]"><DashboardHeader subtitle="Administración" />
      <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#7FD498]" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <DashboardHeader subtitle="Administración" />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#373E45] mb-6">Panel del veterinario</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Hourglass} label="Pendientes" value={stats?.pending} color="#FBBC0C" testid="stat-pending" onClick={() => setActiveTab("requests")} />
          <Stat icon={CheckCircle2} label="Confirmadas" value={stats?.confirmed} color="#7FD498" testid="stat-confirmed" onClick={() => setActiveTab("confirmed")} />
          <Stat icon={CalendarDays} label="Franjas libres" value={stats?.available_slots} color="#9DC3E6" testid="stat-slots" onClick={() => setActiveTab("availability")} />
          <Stat icon={Users} label="Clínicas" value={stats?.clinics} color="#373E45" testid="stat-clinics" onClick={() => setActiveTab("clinics")} />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 rounded-xl p-1 flex-wrap h-auto">
            <TabsTrigger value="agenda" data-testid="tab-agenda" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Agenda</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Solicitudes ({pending.length})</TabsTrigger>
            <TabsTrigger value="confirmed" data-testid="tab-confirmed" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Confirmadas ({confirmed.length})</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">
              Completadas ({completed.length})
              {unreadCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold" data-testid="unread-badge-completed">{unreadCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Disponibilidad</TabsTrigger>
            <TabsTrigger value="clinics" data-testid="tab-clinics" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Clínicas ({clinics.length})</TabsTrigger>
            <TabsTrigger value="tariffs" data-testid="tab-tariffs" className="rounded-lg data-[state=active]:bg-[#7FD498] data-[state=active]:text-[#373E45] font-bold">Tarifas</TabsTrigger>
          </TabsList>

          {/* Agenda */}
          <TabsContent value="agenda" className="mt-6">
            <GoogleCalendarCard />
            <AgendaCalendar bookings={bookings} slots={slots} blockedDays={blockedDays}
              onBlock={(d) => act(() => api.post("/blocked-days", { date: d }), "Día bloqueado")}
              onUnblock={(d) => act(() => api.delete(`/blocked-days/${d}`), "Día desbloqueado")} />
          </TabsContent>


          {/* Requests */}
          <TabsContent value="requests" className="mt-6 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-bold text-[#373E45]">¿Te han reservado por teléfono o email?</p>
                <p className="text-sm text-[#64748B]">Crea tú la cita para la clínica; se confirmará y aparecerá también en su área privada.</p>
              </div>
              <AdminBookingDialog clinics={clinics} slots={slots} onCreated={load} />
            </div>
            <Section title={`Pendientes de confirmar (${pending.length})`}>
              {pending.length === 0 ? <p className="text-sm text-[#64748B]">No hay solicitudes pendientes.</p> :
                pending.map((b) => <BookingRow key={b.id} b={b}
                  onConfirm={() => act(() => api.patch(`/bookings/${b.id}/confirm`), "Reserva confirmada")}
                  onCancel={() => act(() => api.patch(`/bookings/${b.id}/cancel`), "Reserva cancelada")} />)}
            </Section>
            <Section title="Historial de reservas">
              {others.length === 0 ? <p className="text-sm text-[#64748B]">Sin reservas todavía.</p> :
                others.map((b) => <BookingRow key={b.id} b={b}
                  onCancel={b.status === "confirmed" ? () => act(() => api.patch(`/bookings/${b.id}/cancel`), "Reserva cancelada") : null} />)}
            </Section>
          </TabsContent>

          {/* Confirmed appointments */}
          <TabsContent value="confirmed" className="mt-6">
            <Section title={`Citas confirmadas (${confirmed.length})`} icon={CheckCircle2}>
              {confirmed.length === 0 ? <p className="text-sm text-[#64748B]">No hay citas confirmadas.</p> :
                [...confirmed].sort((a, b) => (a.slot_date + a.slot_start).localeCompare(b.slot_date + b.slot_start)).map((b) => (
                  <div key={b.id} className="border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`confirmed-row-${b.id}`}>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-[#373E45] capitalize">{fmtDateLong(b.slot_date)}</span>
                        <span className="text-sm text-[#64748B]">{b.slot_start}–{b.slot_end}</span>
                        <StatusBadge status={b.status} />
                      </div>
                      <p className="text-sm text-[#373E45] font-semibold mt-1">{b.clinic_name}{b.phone ? ` · ${b.phone}` : ""}</p>
                      <PatientDetails b={b} testid={`confirmed-details-${b.id}`} />
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <Button size="sm" onClick={() => act(() => api.patch(`/bookings/${b.id}/complete`), "Cita marcada como realizada")} className="bg-[#373E45] hover:bg-[#2b3137] text-white font-bold rounded-xl shrink-0" data-testid={`complete-${b.id}`}>
                        <CheckCheck className="h-4 w-4 mr-1" />Marcar realizada
                      </Button>
                      <RescheduleBookingButton testid={`reschedule-confirmed-${b.id}`} slots={slots}
                        current={`${fmtDateLong(b.slot_date)} · ${b.slot_start}`}
                        onConfirm={(slotId) => act(() => api.patch(`/bookings/${b.id}/reschedule`, { slot_id: slotId }), "Cita reprogramada")} />
                      <CancelBookingButton testid={`cancel-confirmed-${b.id}`} onConfirm={(reason) => act(() => api.patch(`/bookings/${b.id}/cancel`, { reason }), "Cita cancelada")} />
                    </div>
                  </div>
                ))}
            </Section>
          </TabsContent>

          {/* Completed appointments + reports */}
          <TabsContent value="completed" className="mt-6">
            <Section title={`Citas completadas (${completed.length})`} icon={FileCheck2}>
              <p className="text-sm text-[#64748B] mb-2">Registro de ecografías realizadas. Cada estudio tiene un número de caso único. Añade una nota y sube el informe en PDF para que la clínica pueda consultarlo.</p>
              {completed.length > 0 && (
                <>
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="export-toolbar">
                    <Button size="sm" onClick={() => runExport("zip")} disabled={exporting === "zip"} className="bg-[#373E45] hover:bg-[#2b3137] text-white font-bold rounded-xl" data-testid="export-zip-btn">
                      {exporting === "zip" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileArchive className="h-4 w-4 mr-1.5" />Descargar informes (ZIP)</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => runExport("csv")} disabled={exporting === "csv"} className="rounded-xl border-slate-300 text-[#373E45] font-bold" data-testid="export-csv-btn">
                      {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 mr-1.5" />Exportar CSV (facturación)</>}
                    </Button>
                    <span className="text-xs text-[#64748B] self-center">{(historyFilter.q || historyFilter.clinic_id || historyFilter.from || historyFilter.to) ? "Exporta los estudios del filtro actual" : "Exporta todos los estudios completados"}</span>
                  </div>
                  <StudySearch filter={historyFilter} setFilter={setHistoryFilter} clinics={clinics} resultCount={filteredCompleted.length} />
                </>
              )}
              {completed.length === 0 ? <p className="text-sm text-[#64748B]">Aún no hay citas completadas. Marca una cita confirmada como realizada.</p> :
                filteredCompleted.length === 0 ? <p className="text-sm text-[#64748B]" data-testid="completed-no-results">No hay estudios que coincidan con la búsqueda.</p> :
                [...filteredCompleted].sort((a, b) => (b.slot_date + b.slot_start).localeCompare(a.slot_date + a.slot_start)).map((b) => (
                  <CompletedReportRow key={b.id} b={b} onReload={load} />
                ))}
            </Section>
          </TabsContent>

          {/* Clinics directory */}
          <TabsContent value="clinics" className="mt-6">
            <Section title={`Clínicas registradas (${clinics.length})`} icon={Building2}>
              <div className="flex justify-end -mt-1 mb-2 gap-2 flex-wrap">
                {clinics.length > 0 && (
                  <Button size="sm" variant="outline" onClick={exportClinics} disabled={exporting === "clinics"} className="rounded-xl border-slate-300 text-[#373E45] font-bold" data-testid="export-clinics-btn">
                    {exporting === "clinics" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 mr-1.5" />Exportar clínicas (CSV)</>}
                  </Button>
                )}
                <Button size="sm" onClick={() => setClinicDialog({ open: true, clinic: null })} className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid="add-clinic-btn">
                  <UserPlus className="h-4 w-4 mr-1" />Dar de alta clínica
                </Button>
              </div>
              {clinics.length === 0 ? <p className="text-sm text-[#64748B]">Aún no hay clínicas registradas.</p> :
                <div className="grid sm:grid-cols-2 gap-3">
                  {clinics.map((c) => (
                    <div key={c.id} className={`border rounded-xl p-4 ${c.approved === false ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`} data-testid={`clinic-card-${c.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-bold text-[#373E45]">{c.clinic_name}</p>
                          <p className="text-sm text-[#64748B]">{c.contact_name}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {c.approved === false ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`clinic-status-${c.id}`}><ShieldAlert className="h-3 w-3" />Pendiente</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#2f7a45] bg-[#7FD498]/20 rounded-full px-2 py-0.5" data-testid={`clinic-status-${c.id}`}><ShieldCheck className="h-3 w-3" />Activa</span>
                          )}
                          <span className="text-[10px] font-bold text-[#2f7a45] bg-[#7FD498]/20 rounded-full px-2 py-0.5">{c.total_bookings} reservas</span>
                          <button onClick={() => setClinicDialog({ open: true, clinic: c })} title="Editar" data-testid={`edit-clinic-${c.id}`} className="text-slate-400 hover:text-[#373E45]"><Pencil className="h-4 w-4" /></button>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1 text-sm text-[#64748B]">
                        <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />{c.email}</p>
                        <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{c.phone || "—"}</p>
                        {(c.fiscal_name || c.cif || c.fiscal_address) && (
                          <p className="flex items-center gap-2 text-xs pt-1"><Building2 className="h-3.5 w-3.5" />{[c.fiscal_name, c.cif, c.fiscal_address].filter(Boolean).join(" · ")}</p>
                        )}
                        <p className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5" />{c.confirmed_bookings} confirmadas</p>
                        <p className="flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" />Alta: {c.created_at ? c.created_at.slice(0, 10) : "—"}</p>
                        <p className="flex items-center gap-2 font-semibold text-[#373E45]" data-testid={`clinic-lastseen-${c.id}`}><LogIn className="h-3.5 w-3.5" />Último acceso: {c.last_seen_at ? new Date(c.last_seen_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Sin accesos"}</p>
                      </div>
                      {c.approved === false && (
                        <Button size="sm" onClick={() => act(() => api.patch(`/clinics/${c.id}/approval`, { approved: true }), "Cuenta activada")} className="mt-3 w-full rounded-xl bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold" data-testid={`approve-clinic-${c.id}`}>
                          <ShieldCheck className="h-4 w-4 mr-1.5" />Activar cuenta
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openClinicHistory(c.id)} className="mt-3 w-full rounded-xl border-[#9DC3E6] text-[#373E45] hover:bg-[#9DC3E6]/15 font-bold" data-testid={`clinic-history-${c.id}`}>
                        <History className="h-4 w-4 mr-1.5" />Historial de estudios
                      </Button>
                    </div>
                  ))}
                </div>}
            </Section>
          </TabsContent>

          {/* Tariffs */}
          <TabsContent value="tariffs" className="mt-6">
            <TariffsManager />
          </TabsContent>

          {/* Availability */}
          <TabsContent value="availability" className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <Section title="Horario semanal recurrente" icon={Repeat}>
                <p className="text-sm text-[#64748B] mb-4">Genera franjas automáticamente para las próximas 8 semanas.</p>
                <div className="space-y-3 mb-4">
                  {rules.length === 0 ? <p className="text-sm text-[#64748B]">Sin horarios definidos.</p> :
                    rules.map((r) => (
                      <div key={r.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-2.5" data-testid={`rule-${r.id}`}>
                        <span className="text-sm font-semibold text-[#373E45]">{WEEKDAYS[r.weekday]} · {r.start_time}–{r.end_time} · {r.slot_minutes} min</span>
                        <button onClick={() => act(() => api.delete(`/availability/rules/${r.id}`), "Horario eliminado")} data-testid={`delete-rule-${r.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-[#373E45] font-semibold text-sm">Día</Label>
                    <Select value={rule.weekday} onValueChange={(v) => setRule({ ...rule, weekday: v })}>
                      <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="rule-weekday-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{WEEKDAYS.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[#373E45] font-semibold text-sm">Inicio</Label><TimeSelect value={rule.start_time} onChange={(v) => setRule({ ...rule, start_time: v })} testid="rule-start-input" /></div>
                  <div><Label className="text-[#373E45] font-semibold text-sm">Fin</Label><TimeSelect value={rule.end_time} onChange={(v) => setRule({ ...rule, end_time: v })} testid="rule-end-input" /></div>
                  <div className="col-span-2"><Label className="text-[#373E45] font-semibold text-sm">Duración de cada franja (min)</Label>
                    <Select value={rule.slot_minutes} onValueChange={(v) => setRule({ ...rule, slot_minutes: v })}>
                      <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="rule-duration-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{["15", "30", "45", "60", "90"].map((m) => <SelectItem key={m} value={m}>{m} min</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={addRule} className="mt-4 w-full bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid="add-rule-btn"><Plus className="h-4 w-4 mr-1" />Añadir horario</Button>
              </Section>

              <Section title="Añadir bloque de días" icon={CalendarRange}>
                <p className="text-sm text-[#64748B] mb-4">Genera franjas para un rango de fechas y días de la semana. Ej.: del 29 jun al 3 jul, lun–vie, 9:00–14:00, citas de 90 min.</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div><Label className="text-[#373E45] font-semibold text-sm">Desde</Label><Input type="date" min={todayISO()} value={block.start_date} onChange={(e) => setBlock({ ...block, start_date: e.target.value })} className="mt-1.5 bg-slate-50" data-testid="block-start-date-input" /></div>
                  <div><Label className="text-[#373E45] font-semibold text-sm">Hasta</Label><Input type="date" min={block.start_date} value={block.end_date} onChange={(e) => setBlock({ ...block, end_date: e.target.value })} className="mt-1.5 bg-slate-50" data-testid="block-end-date-input" /></div>
                </div>
                <Label className="text-[#373E45] font-semibold text-sm">Días de la semana</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5 mb-3" data-testid="block-weekdays">
                  {WEEKDAYS.map((w, i) => (
                    <button key={i} onClick={() => toggleWeekday(i)} data-testid={`block-weekday-${i}`}
                      className={`h-9 w-11 rounded-lg text-xs font-bold border transition-colors ${block.weekdays.includes(i) ? "bg-[#7FD498] border-[#7FD498] text-[#373E45]" : "bg-slate-50 border-slate-200 text-[#64748B] hover:border-slate-300"}`}>
                      {w.slice(0, 3)}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[#373E45] font-semibold text-sm">Inicio</Label><TimeSelect value={block.start_time} onChange={(v) => setBlock({ ...block, start_time: v })} testid="block-start-time-input" /></div>
                  <div><Label className="text-[#373E45] font-semibold text-sm">Fin</Label><TimeSelect value={block.end_time} onChange={(v) => setBlock({ ...block, end_time: v })} testid="block-end-time-input" /></div>
                  <div className="col-span-2"><Label className="text-[#373E45] font-semibold text-sm">Duración de cada franja (min)</Label>
                    <Select value={block.slot_minutes} onValueChange={(v) => setBlock({ ...block, slot_minutes: v })}>
                      <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="block-duration-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{["15", "30", "45", "60", "90"].map((m) => <SelectItem key={m} value={m}>{m} min</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={addRange} className="mt-4 w-full bg-[#9DC3E6] hover:bg-[#86b2dc] text-[#373E45] font-bold rounded-xl" data-testid="add-range-btn"><Plus className="h-4 w-4 mr-1" />Generar franjas del bloque</Button>
              </Section>
            </div>

            <Section title="Franjas disponibles próximas" icon={CalendarClock}>
              {grouped.length === 0 ? <p className="text-sm text-[#64748B]">No hay franjas libres. Añade un horario o una franja puntual.</p> : (
                <div className="space-y-5 max-h-[600px] overflow-y-auto pr-1" data-testid="admin-slots">
                  {grouped.map(({ date, slots }) => (
                    <div key={date}>
                      <h4 className="text-sm font-bold text-[#373E45] capitalize mb-2">{fmtDateLong(date)}</h4>
                      <div className="flex flex-wrap gap-2">
                        {slots.map((s) => (
                          <span key={s.id} className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-semibold text-[#373E45]" data-testid={`admin-slot-${s.id}`}>
                            <Clock className="h-3.5 w-3.5 text-[#7FD498]" />{s.start_time}
                            <button onClick={() => act(() => api.delete(`/slots/${s.id}`), "Franja eliminada")} data-testid={`delete-slot-${s.id}`} className="text-slate-300 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </TabsContent>
        </Tabs>
      </main>
      <ClinicDialog open={clinicDialog.open} clinic={clinicDialog.clinic}
        onOpenChange={(o) => setClinicDialog((s) => ({ ...s, open: o }))} onSaved={load} />
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, testid, onClick }) {
  return (
    <button type="button" onClick={onClick} className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-[#7FD498] hover:shadow-sm transition-all cursor-pointer" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-[#64748B]">{label}</p>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${color}25` }}><Icon className="h-5 w-5" style={{ color }} /></div>
      </div>
      <p className="text-3xl font-extrabold text-[#373E45] mt-2">{value ?? 0}</p>
    </button>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-bold text-[#373E45] flex items-center gap-2 mb-4">{Icon && <Icon className="h-5 w-5 text-[#7FD498]" />}{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function BookingRow({ b, onConfirm, onCancel }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`admin-booking-${b.id}`}>
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-bold text-[#373E45] capitalize">{fmtDateLong(b.slot_date)}</span>
          <span className="text-sm text-[#64748B]">{b.slot_start}–{b.slot_end}</span>
          <StatusBadge status={b.status} />
        </div>
        <p className="text-sm text-[#373E45] font-semibold">{b.clinic_name} · {b.contact_name}{b.phone ? ` · ${b.phone}` : ""}</p>
        <PatientDetails b={b} testid={`admin-booking-details-${b.id}`} />
      </div>
      <div className="flex gap-2">
        {onConfirm && <Button size="sm" onClick={onConfirm} className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid={`confirm-${b.id}`}><Check className="h-4 w-4 mr-1" />Confirmar</Button>}
        {onCancel && <Button size="sm" variant="outline" onClick={onCancel} className="rounded-xl border-slate-300 text-red-600 hover:text-red-700" data-testid={`reject-${b.id}`}><X className="h-4 w-4 mr-1" />Cancelar</Button>}
      </div>
    </div>
  );
}
